"use client";

import { c as _c } from "react/compiler-runtime";
import { AlertCircle, AudioLines, Download } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { memo, useEffect, useState } from "react";
import { Audio } from "@/components/ai-elements/audio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { downloadAudio } from "@/lib/audio-gen";
import { audioStudioSuggestions, sampleSuggestions } from "@/lib/hero-suggestions";
import type { AudioGalleryItem, GeneratedAudio } from "@/lib/audio-gen";
interface AudioGalleryProps {
  items: AudioGalleryItem[];
  comparisonMode: boolean;
  onSuggestionClick?: (prompt: string) => void;
}
const GalleryAudio = memo(t0 => {
  const $ = _c(13);
  const {
    audio,
    modelName
  } = t0;
  const t1 = modelName ? `${modelName} audio` : "Generated audio";
  let t2;
  if ($[0] !== audio.base64 || $[1] !== audio.mediaType || $[2] !== t1) {
    t2 = <Audio base64={audio.base64} mediaType={audio.mediaType} aria-label={t1} className="min-w-0 flex-1" />;
    $[0] = audio.base64;
    $[1] = audio.mediaType;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = modelName ? `Download ${modelName} audio` : "Download audio";
  let t4;
  if ($[4] !== audio) {
    t4 = () => downloadAudio(audio);
    $[4] = audio;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Download className="h-4 w-4" />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t4) {
    t6 = <Button variant="secondary" size="icon" className="h-8 w-8 shrink-0" aria-label={t3} onClick={t4}>{t5}</Button>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t2 || $[11] !== t6) {
    t7 = <div className="flex items-center gap-2 rounded-lg border p-3">{t2}{t6}</div>;
    $[10] = t2;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
});
function LoadingSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="h-16 rounded-lg max-w-xl" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function EmptyState(t0) {
  const $ = _c(10);
  const {
    onSuggestionClick
  } = t0;
  const [suggestions, setSuggestions] = useState(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setSuggestions(sampleSuggestions(audioStudioSuggestions, 6));
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <AudioLines className="h-16 w-16 text-muted-foreground/30 mb-6" />;
    t4 = <h3 className="text-lg font-medium mb-2">No audio yet</h3>;
    t5 = <p className="text-sm text-muted-foreground mb-8 max-w-md">Enter the text you want to hear and click Generate to get started.</p>;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] !== onSuggestionClick || $[6] !== suggestions) {
    t6 = suggestions ? <motion.div key="suggestions" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.07,
      ease: "easeOut"
    }} className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl w-full">{suggestions.map((s, index) => <motion.button key={s} type="button" initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.12,
        delay: index * 0.025,
        ease: "easeOut"
      }} onClick={() => onSuggestionClick?.(s)} className="rounded-md border px-4 py-3 text-left text-sm hover:bg-muted/60 transition-colors">{s}</motion.button>)}</motion.div> : null;
    $[5] = onSuggestionClick;
    $[6] = suggestions;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = <div className="flex flex-col items-center justify-center py-20 text-center">{t3}{t4}{t5}<AnimatePresence>{t6}</AnimatePresence></div>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7;
}
function SingleModeItem(t0) {
  const $ = _c(20);
  const {
    item
  } = t0;
  const model = item.models[0];
  if (!model) {
    return null;
  }
  let t1;
  if ($[0] !== item.prompt) {
    t1 = <p className="text-sm text-muted-foreground truncate flex-1">{item.prompt}</p>;
    $[0] = item.prompt;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== item.voice) {
    t2 = item.voice && <Badge variant="outline" className="text-xs shrink-0">{item.voice}</Badge>;
    $[2] = item.voice;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== item.timestamp) {
    t3 = new Date(item.timestamp).toLocaleTimeString();
    $[4] = item.timestamp;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <span className="text-xs text-muted-foreground shrink-0">{t3}</span>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t4) {
    t5 = <div className="flex items-center gap-2">{t1}{t2}{t4}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== model.audio || $[13] !== model.error || $[14] !== model.isLoading || $[15] !== model.modelName) {
    t6 = model.error ? <div className="flex items-center gap-2 p-4 rounded-lg border border-destructive/50 bg-destructive/5"><AlertCircle className="h-4 w-4 text-destructive shrink-0" /><p className="text-sm text-destructive">{model.error}</p></div> : model.isLoading ? <LoadingSkeleton /> : model.audio ? <div className="max-w-xl"><GalleryAudio audio={model.audio} modelName={model.modelName} /></div> : null;
    $[12] = model.audio;
    $[13] = model.error;
    $[14] = model.isLoading;
    $[15] = model.modelName;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t5 || $[18] !== t6) {
    t7 = <div className="space-y-2">{t5}{t6}</div>;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
function ComparisonModeItem(t0) {
  const $ = _c(20);
  const {
    item
  } = t0;
  let t1;
  if ($[0] !== item.prompt) {
    t1 = <p className="text-sm text-muted-foreground truncate flex-1">{item.prompt}</p>;
    $[0] = item.prompt;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== item.voice) {
    t2 = item.voice && <Badge variant="outline" className="text-xs shrink-0">{item.voice}</Badge>;
    $[2] = item.voice;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== item.timestamp) {
    t3 = new Date(item.timestamp).toLocaleTimeString();
    $[4] = item.timestamp;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <span className="text-xs text-muted-foreground shrink-0">{t3}</span>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t4) {
    t5 = <div className="flex items-center gap-2">{t1}{t2}{t4}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = `grid gap-4 ${item.models.length === 1 ? "grid-cols-1" : item.models.length === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`;
  let t7;
  if ($[12] !== item.models) {
    t7 = item.models.map(_temp);
    $[12] = item.models;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6 || $[15] !== t7) {
    t8 = <div className={t6}>{t7}</div>;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t5 || $[18] !== t8) {
    t9 = <div className="space-y-2">{t5}{t8}</div>;
    $[17] = t5;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
}
function _temp(model, idx) {
  return <div key={`${model.modelId}-${idx}`} className="space-y-2"><Badge variant="outline" className="text-xs">{model.modelName}</Badge>{model.error ? <div className="flex items-center gap-2 p-4 rounded-lg border border-destructive/50 bg-destructive/5"><AlertCircle className="h-4 w-4 text-destructive shrink-0" /><p className="text-sm text-destructive">{model.error}</p></div> : model.isLoading ? <LoadingSkeleton /> : model.audio ? <GalleryAudio audio={model.audio} modelName={model.modelName} /> : null}</div>;
}
export function AudioGallery(t0) {
  const $ = _c(9);
  const {
    items,
    comparisonMode,
    onSuggestionClick
  } = t0;
  if (items.length === 0) {
    let t1;
    if ($[0] !== onSuggestionClick) {
      t1 = <EmptyState onSuggestionClick={onSuggestionClick} />;
      $[0] = onSuggestionClick;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== comparisonMode || $[3] !== items) {
    let t2;
    if ($[5] !== comparisonMode) {
      t2 = item => <div key={item.id} id={`gallery-${item.id}`}>{comparisonMode ? <ComparisonModeItem item={item} /> : <SingleModeItem item={item} />}</div>;
      $[5] = comparisonMode;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = items.map(t2);
    $[2] = comparisonMode;
    $[3] = items;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = <div className="space-y-8">{t1}</div>;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}